import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { retryRoom, type ConnStatus } from './connection';

const LABEL: Record<ConnStatus, string> = {
  connecting: 'Connecting',
  live: 'Live',
  reconnecting: 'Reconnecting',
  unreachable: 'Offline',
  refused: 'Not let in',
  closed: 'Offline',
};

const COLOR: Record<ConnStatus, string> = {
  connecting: 'var(--amber)',
  live: 'var(--green)',
  reconnecting: 'var(--amber)',
  unreachable: 'var(--red)',
  refused: 'var(--red)',
  closed: 'var(--red)',
};

/** Marks the history entry pushed to catch the back gesture. */
const GUARD = 'ofc-leave-guard';

/** Asks the app to show how to play whichever game this is. */
export const HELP_EVENT = 'ofc:help';

/**
 * Set by the app while a room belongs to a game night, so the top bar can offer
 * the way back to it from inside any game.
 */
export const LoungeLink = createContext<{
  code: string;
  open(): void;
  /** Whether this phone runs the night: it can bring everyone back to pick again. */
  host: boolean;
  recall(): void;
} | null>(null);

/**
 * Room code, connection state and the way out. The code stays on screen the whole
 * time so a latecomer can always be told what to type.
 *
 * With `guard` set — a game is under way — leaving asks first. The arrow is a
 * small target in the corner a thumb reaches for without looking, and because the
 * app routes on the URL hash, an iPhone edge-swipe or the Android back gesture
 * used to drop you out of a round just as instantly.
 */
export function ConnectionBar({
  status,
  roomCode,
  onLeave,
  guard = false,
  paused = false,
  onPause,
  onResume,
  help = true,
  onSkip,
  skipLabel,
  onEndGame,
}: {
  status: ConnStatus;
  roomCode: string;
  onLeave(): void;
  guard?: boolean;
  /** The host has the clock stopped. */
  paused?: boolean;
  /** Given to the host while there is a clock to stop. */
  onPause?(): void;
  /** Given to the host while the clock is stopped. */
  onResume?(): void;
  /** Offer the how-to-play slides. */
  help?: boolean;
  /** Given to the host while there is a prompt they could throw away. */
  onSkip?(): void;
  /** What skipping is called in this game: "Skip this question", "New card"… */
  skipLabel?: string;
  /** Given to the host while a game is under way. */
  onEndGame?(): void;
}) {
  const [asking, setAsking] = useState(false);
  const [menu, setMenu] = useState(false);
  const hostMenu = Boolean((onPause && !paused) || onSkip || onEndGame);
  const lounge = useContext(LoungeLink);

  useLeaveGuard(guard, () => setAsking(true));

  const requestLeave = () => (guard ? setAsking(true) : onLeave());

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button
          className="btn-ghost"
          onClick={requestLeave}
          aria-label="Leave this room"
          style={{ minHeight: 44, minWidth: 44, padding: '4px 10px', border: 0 }}
        >
          <span className="muted">←</span>
        </button>

        <div className="row" style={{ gap: 6 }}>
          <div
            className="sign"
            style={{ letterSpacing: '0.3em', fontSize: 20 }}
            aria-label={`Room code ${roomCode.split('').join(' ')}`}
          >
            {roomCode}
          </div>
        </div>

        <div className="row small muted" style={{ gap: 4 }}>
          {help && (
            <button
              className="btn-ghost"
              aria-label="How to play"
              onClick={() => window.dispatchEvent(new Event(HELP_EVENT))}
              style={{ minHeight: 36, minWidth: 36, padding: 0, borderRadius: '50%' }}
            >
              ?
            </button>
          )}
          {hostMenu && (
            <button
              className="btn-ghost"
              aria-label="Host controls"
              aria-haspopup="dialog"
              onClick={() => setMenu(true)}
              style={{ minHeight: 36, minWidth: 36, padding: 0, borderRadius: '50%' }}
            >
              ⋯
            </button>
          )}
          <span
            aria-hidden
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: COLOR[status],
              boxShadow: `0 0 8px ${COLOR[status]}`,
              // A pulse makes a stalled reconnect obvious without reading the label.
              animation: status === 'reconnecting' ? 'flicker 1.2s infinite' : undefined,
            }}
          />
          <span>{LABEL[status]}</span>
        </div>
      </div>

      {lounge && lounge.code !== roomCode && <GameNightBar lounge={lounge} />}

      {/* A while without the room: say so plainly, with a way to try now or go. */}
      {status === 'unreachable' && (
        <div
          role="alert"
          className="panel col"
          style={{ gap: 8, padding: '10px 12px', borderColor: 'var(--red)' }}
        >
          <span className="sign" style={{ color: 'var(--red)', fontSize: 14 }}>
            Can't reach the room
          </span>
          <span className="small muted">
            Still trying in the background. Anything you send will go when you are back.
          </span>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn-primary grow small" onClick={retryRoom} style={{ minHeight: 40 }}>
              Try again
            </button>
            <button className="btn-ghost grow small" onClick={onLeave} style={{ minHeight: 40 }}>
              Leave
            </button>
          </div>
        </div>
      )}
      {status === 'reconnecting' && (
        <p className="small center" role="status" style={{ margin: 0, color: 'var(--amber)' }}>
          Reconnecting… anything you send will go when you are back.
        </p>
      )}

      {paused && (
        <div
          role="status"
          className="panel row"
          style={{ gap: 10, padding: '8px 12px', borderColor: 'var(--amber)', justifyContent: 'space-between' }}
        >
          <span className="sign" style={{ color: 'var(--amber)', fontSize: 14 }}>
            ⏸ Paused
          </span>
          {onResume ? (
            <button className="btn-primary small" onClick={onResume} style={{ minHeight: 36 }}>
              Resume
            </button>
          ) : (
            <span className="small muted">The host stopped the clock</span>
          )}
        </div>
      )}

      {menu && (
        <HostSheet
          onClose={() => setMenu(false)}
          onPause={onPause && !paused ? onPause : undefined}
          onSkip={onSkip}
          skipLabel={skipLabel ?? 'Skip this prompt'}
          onEndGame={onEndGame}
        />
      )}

      {asking && (
        <LeaveSheet
          roomCode={roomCode}
          onStay={() => setAsking(false)}
          onLeave={() => {
            setAsking(false);
            onLeave();
          }}
        />
      )}
    </>
  );
}

/**
 * Catches the back gesture while a game is running.
 *
 * A same-address history entry is pushed on top, so going back lands on a page
 * that looks identical and fires `popstate` rather than changing the hash. That
 * is the moment to ask; if they stay, the entry goes straight back on so the next
 * swipe is caught too.
 *
 * The entry is only ever pushed when one is not already there, which keeps
 * StrictMode's double-run from stacking two of them.
 */
function useLeaveGuard(active: boolean, onBack: () => void): void {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    if (!active) return;

    const armed = () => (history.state as { [GUARD]?: boolean } | null)?.[GUARD] === true;
    const arm = () => history.pushState({ [GUARD]: true }, '', location.href);

    if (!armed()) arm();

    const onPop = () => {
      // Back past our entry: the address is unchanged, so nothing else noticed.
      if (armed()) return;
      arm();
      onBackRef.current();
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [active]);
}

/**
 * The host's controls, in one place so the top bar stays small on a phone:
 * stop the clock, throw away a bad prompt, or end the game early. Ending asks
 * twice, since there is no undoing it.
 */
function HostSheet({
  onClose,
  onPause,
  onSkip,
  skipLabel,
  onEndGame,
}: {
  onClose(): void;
  onPause?(): void;
  onSkip?(): void;
  skipLabel: string;
  onEndGame?(): void;
}) {
  const [ending, setEnding] = useState(false);
  const run = (action: () => void) => () => {
    action();
    onClose();
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'color-mix(in srgb, var(--ink) 72%, transparent)',
        display: 'grid',
        alignItems: 'end',
        padding:
          '14px calc(var(--safe-right) + 14px) calc(var(--safe-bottom) + 14px) calc(var(--safe-left) + 14px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Host controls"
        className="panel col"
        onClick={(event) => event.stopPropagation()}
        style={{ gap: 10, maxWidth: 480, width: '100%', margin: '0 auto', padding: '16px' }}
      >
        <h2 className="sign" style={{ margin: 0, fontSize: 16 }}>
          Host controls
        </h2>
        {onPause && (
          <button className="btn-ghost" onClick={run(onPause)} style={{ minHeight: 48 }}>
            ⏸ Pause the clock
          </button>
        )}
        {onSkip && (
          <button className="btn-ghost" onClick={run(onSkip)} style={{ minHeight: 48 }}>
            ⏭ {skipLabel}
          </button>
        )}
        {onEndGame &&
          (ending ? (
            <button
              className="btn-primary"
              onClick={run(onEndGame)}
              style={{ minHeight: 48, background: 'var(--red)', borderColor: 'var(--red)' }}
            >
              Yes, end it now
            </button>
          ) : (
            <button className="btn-ghost" onClick={() => setEnding(true)} style={{ minHeight: 48, color: 'var(--red)' }}>
              ■ End the game
            </button>
          ))}
        <button className="btn-ghost small" onClick={onClose} style={{ border: 0 }}>
          Never mind
        </button>
      </div>
    </div>
  );
}

function LeaveSheet({
  roomCode,
  onStay,
  onLeave,
}: {
  roomCode: string;
  onStay(): void;
  onLeave(): void;
}) {
  const stayRef = useRef<HTMLButtonElement>(null);
  // Held in a ref so the effect runs once: game screens re-render on every tick
  // of their timers, and re-running it would drag focus back to Stay each time.
  const onStayRef = useRef(onStay);
  onStayRef.current = onStay;

  // Staying is the safe answer, so it has the focus — a stray Enter keeps you in.
  useEffect(() => {
    stayRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onStayRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      role="presentation"
      onClick={onStay}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'color-mix(in srgb, var(--ink) 72%, transparent)',
        display: 'grid',
        alignItems: 'end',
        padding:
          '14px calc(var(--safe-right) + 14px) calc(var(--safe-bottom) + 14px) calc(var(--safe-left) + 14px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-title"
        className="panel col"
        onClick={(event) => event.stopPropagation()}
        style={{ gap: 12, maxWidth: 480, width: '100%', margin: '0 auto', padding: '18px 16px' }}
      >
        <h2 id="leave-title" className="sign" style={{ margin: 0, fontSize: 18 }}>
          Leave the game?
        </h2>
        <p className="small muted" style={{ margin: 0, lineHeight: 1.45 }}>
          Your seat and score are kept. Come back with code{' '}
          <strong style={{ color: 'var(--text)', letterSpacing: '0.15em' }}>{roomCode}</strong> and
          you carry on where you left off.
        </p>
        <div className="row" style={{ gap: 8 }}>
          <button ref={stayRef} className="btn-primary grow" onClick={onStay}>
            Stay
          </button>
          <button className="btn-ghost grow" onClick={onLeave}>
            Leave
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Inside a game night's game: the way back to the lounge, in plain sight. The host
 * brings everybody back to pick another game in one tap; anybody else can step
 * back to the lounge on their own.
 */
function GameNightBar({ lounge }: { lounge: NonNullable<React.ContextType<typeof LoungeLink>> }) {
  // Sent, until the lounge sends everybody back: no second tap needed or taken.
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (!sent) return;
    const timer = setTimeout(() => setSent(false), 6000);
    return () => clearTimeout(timer);
  }, [sent]);
  return (
    <div className="panel row" style={{ gap: 8, padding: '6px 10px', borderColor: 'var(--amber)' }}>
      <span className="grow small" style={{ color: 'var(--amber)' }}>
        🌙 Game night <span className="muted">· {lounge.code}</span>
      </span>
      {lounge.host ? (
        <button
          className="btn-ghost small"
          disabled={sent}
          onClick={() => {
            setSent(true);
            lounge.recall();
          }}
          style={{ minHeight: 36, borderColor: 'var(--amber)', color: 'var(--amber)' }}
        >
          {sent ? 'Bringing everyone back…' : 'Change game'}
        </button>
      ) : (
        <button className="btn-ghost small" onClick={lounge.open} style={{ minHeight: 36, borderColor: 'var(--amber)', color: 'var(--amber)' }}>
          Back to game night
        </button>
      )}
    </div>
  );
}
